import React from 'react';
import { useBundle } from '@amzn/react-arb-tools';
import { t, resolveContent } from 'src/utils/translation';
import { BUNDLE_NAME, LEARNING_PATHWAY_STRING_ID, CREDENTIAL_STRING_ID } from 'src/constants/localization';
import { PageHeaderV2, PageHeaderV2Props } from '@amzn/seller-university-ui-components';
import { LearningPathDetailData } from 'src/types/state/learningPathway';

interface LearningPathwayHeaderV2Props {
  title: string;
  description: string;
  imageSrc?: string;
  stats: LearningPathDetailData;
  isLocalized?: boolean;
  progress?: PageHeaderV2Props['progress'];
}

const LearningPathwayPageHeaderV2: React.FC<LearningPathwayHeaderV2Props> = ({
  title,
  description,
  imageSrc,
  stats,
  isLocalized = false,
  progress,
}) => {
  const [learningPathwayBundle] = useBundle(BUNDLE_NAME.LEARNING_PATHWAY);
  const [credentialBundle] = useBundle(BUNDLE_NAME.CREDENTIAL);

  // Credential content carries `credential-`-prefixed string IDs that live in the credential
  // bundle and are never pre-localized, so they must always resolve against it. Other pathways
  // keep resolving their title/description against the learning-pathway bundle, honoring
  // isLocalized for content that may already be localized (PolyD).
  const resolveHeaderContent = (content: string) => (
    content.startsWith('credential-')
      ? t(credentialBundle, content)
      : resolveContent(learningPathwayBundle, content, isLocalized)
  );

  const formatLearningPathDetails = (statsData: LearningPathDetailData) => {
    return {
      learningPathType: t(
        learningPathwayBundle,
        LEARNING_PATHWAY_STRING_ID.LEARNING_PATHWAY_PAGE_HEADER_PATH_TYPE,
      ),
      totalSections: t(
        credentialBundle,
        CREDENTIAL_STRING_ID.TOTAL_SECTIONS,
        { count: statsData.totalSections || 0 },
      ),
      totalModules: t(
        credentialBundle,
        CREDENTIAL_STRING_ID.TOTAL_MODULES,
        { count: statsData.totalModules ?? ((statsData.totalDocuments || 0) + (statsData.totalVideos || 0)) },
      ),
      duration: t(
        learningPathwayBundle,
        LEARNING_PATHWAY_STRING_ID.LEARNING_PATHWAY_DURATION,
        {
          minutes: Math.round((statsData.totalDuration || 0) / 60),
        },
      ),
    };
  };

  return (
    <PageHeaderV2
      title={resolveHeaderContent(title)}
      description={resolveHeaderContent(description)}
      imageSrc={imageSrc}
      learningPathDetail={formatLearningPathDetails(stats)}
      progress={progress}
    />
  );
};

export default LearningPathwayPageHeaderV2;
